import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';
import { z } from 'zod';
import { ENDPOINT, PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { zodAddressSchema, zodPaginatedResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { toSnakeCase, withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import { DATA_EXCHANGE_STATUS } from './constants';
import { queryKeysCompany } from './queryKeys';

const ORDER_BY = [
  'current_issues',
  'id',
  'is_receiver',
  'is_sender',
  'known_issues',
  'name',
] as const;

const zodCompaniesAdminQueryParams = z
  .object({
    companyAccountId: z.uuid().optional(),
    isActive: z.boolean().optional(),
    isReceiver: z.boolean().optional(),
    isSender: z.boolean().optional(),
    limit: z.number().min(1).max(500).prefault(PAGINATION_PAGE_SIZE_DEFAULT),
    offset: z.number().nonnegative().prefault(0),
    orderBy: z
      .preprocess((value) => toSnakeCase(String(value)), z.enum(ORDER_BY))
      .prefault('name'),
    returnTotalCount: z.boolean().prefault(true),
    searchString: z.string().optional(),
    sort: z
      .preprocess(
        (value) => String(value).toUpperCase(),
        z.enum(['ASC', 'DESC']),
      )
      .prefault('ASC'),
    statusDln: z
      .preprocess(
        (value) => toSnakeCase(String(value)),
        z.enum(DATA_EXCHANGE_STATUS),
      )
      .optional(),
    statusInvoice: z
      .preprocess(
        (value) => toSnakeCase(String(value)),
        z.enum(DATA_EXCHANGE_STATUS),
      )
      .optional(),
  })
  .refine((data) => {
    // Only one filter can be set at a time (see API documentation).
    const filters = [
      'companyAccountId',
      'isReceiver',
      'isSender',
      'statusDLn',
      'statusInvoice',
    ];

    const setFilters = filters.filter(
      (filter) => !['', undefined, null].includes(data[filter]),
    );
    if (setFilters.length > 1) {
      log.error({
        message: `Only one filter can be set at a time! Currently set: ${setFilters.join(', ')}`,
        error: 'error',
      });

      // Set all setFilters but the first to undefined.
      // This is quite random as a fallback solution. Maybe we should just throw?
      for (const filter of setFilters.slice(1)) {
        data[filter] = undefined;
      }
    }

    return data;
  })
  .transform((data) => withoutObjectKeysWhereValueIs(data, [undefined, '']));

export type CompaniesAdminQueryParams = z.infer<
  typeof zodCompaniesAdminQueryParams
>;

/**
 * `AllCompanyResponseAdmin` in the OpenAPI spec. Only `id`, `name`,
 * `companyAccountId` and `companyAccountName` are required — every other
 * field is nullable on the wire.
 */
export const zodCompanyAdminListItemSchema = z.object({
  address: zodAddressSchema.nullish(),
  blacklisted: z.boolean().nullable(),
  companyAccountId: z.uuid(),
  companyAccountName: z.string(),
  createdOn: z.iso.datetime({ offset: true }).nullish(),
  currentIssues: z.string().nullable(),
  furtherInformation: z.string().nullable(),
  id: z.uuid(),
  isActive: z.boolean().nullable(),
  isReceiver: z.boolean().nullable(),
  isSender: z.boolean().nullable(),
  knownIssues: z.string().nullable(),
  name: z.string(),
  statusDln: z.enum(DATA_EXCHANGE_STATUS).nullable(),
  statusInvoice: z.enum(DATA_EXCHANGE_STATUS).nullable(),
});

export type CompanyAdminListItem = z.infer<
  typeof zodCompanyAdminListItemSchema
>;

/**
 * `PaginatedCompanyResponseAdmin` in the OpenAPI spec.
 */
const zodCompaniesAdminResponseSchema = zodPaginatedResponseSchema(
  zodCompanyAdminListItemSchema,
);

type CompaniesAdminResponse = z.infer<typeof zodCompaniesAdminResponseSchema>;

/**
 * Generates the query options for the companies Admin query so they can be shared between the useQuery hook and the prefetching.
 */
export const getCompaniesAdminQueryOptions = ({
  queryParameters,
  options = {},
}: {
  queryParameters: Partial<CompaniesAdminQueryParams>;
  options?: Omit<
    UseQueryOptions<CompaniesAdminResponse>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const validatedSearchParams =
    zodCompaniesAdminQueryParams.parse(queryParameters);

  return {
    queryFn: async () => fetchCompaniesAdmin(validatedSearchParams),
    queryKey: queryKeysCompany.getAllAdmin(validatedSearchParams),
    staleTime: ms('60s'), // prevent hitting the rate limit of 30 requests/s
    ...options,
  };
};

/**
 * Fetches all companies from the API an ADMIN has access to.
 * Does not limit the results by the user's permissions as the GET_ALL endpoint does. Requires ADMIN access.
 * @param queryParameters - The query parameters for fetching companies.
 * @returns The company data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_companies_vadmin_company_get
 */
export const fetchCompaniesAdmin = async (
  queryParameters: Partial<CompaniesAdminQueryParams> = {},
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.COMPANY.GET_ALL_ADMIN(), {
        searchParams: queryParameters,
        validate: {
          response: zodCompaniesAdminResponseSchema,
          searchParams: zodCompaniesAdminQueryParams,
        },
      })
      .json<CompaniesAdminResponse>();

    return (
      response ?? {
        data: [],
        hasNextPage: false,
        paginatedCount: 0,
        totalCount: 0,
      }
    );
  } catch (error) {
    log.error({
      message: 'Error fetching companies',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the data for all companies with given query parameters an ADMIN has access to.
 * Does not limit the results by the user's permissions as the GET_ALL endpoint does. Requires ADMIN access.
 * Handles pagination and prefetches the next page of companies for better performance.
 *
 * @param queryParameters - The query parameters for fetching companies.
 * @param options - Additional options for the useQuery hook.
 * @returns A list of companies.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_companies_vadmin_company_get
 */
export const useQueryCompaniesAdmin = (
  queryParameters: Partial<CompaniesAdminQueryParams>,
  options?: Parameters<typeof useQuery>[0],
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery(
      getCompaniesAdminQueryOptions({
        options,
        queryParameters: {
          ...queryParameters,
          offset:
            (queryParameters.offset ?? 0) +
            (queryParameters.limit ?? PAGINATION_PAGE_SIZE_DEFAULT),
        },
      }),
    );
  }, [queryParameters, options, queryClient]);

  return useQuery<CompaniesAdminResponse>({
    ...getCompaniesAdminQueryOptions({ options, queryParameters }),
    placeholderData: keepPreviousData,
  });
};
